import type { GanttSpan } from "@/ds";

import { GANTT_MONTH_LABELS } from "./gantt.constants";
import type {
  GanttIsoDate,
  GanttPosition,
  GanttRange,
  GanttScale,
  GanttScaleBand,
  GanttUnit,
} from "./types-gantt";

const MS_PER_DAY = 86_400_000;

const pad = (value: number) => String(value).padStart(2, "0");

/** `AAAA-MM-DD` → número do dia (UTC), sem deslocamento de fuso. */
export function toDayNumber(date: GanttIsoDate): number {
  const [year = 1970, month = 1, day = 1] = date.slice(0, 10).split("-").map(Number);
  return Math.floor(Date.UTC(year, month - 1, day) / MS_PER_DAY);
}

export interface BuildGanttScaleOptions extends GanttRange {
  /** Dia (UTC) em que a rolagem horizontal começa; sem ele, no início do eixo. */
  focusDay?: number;
  /** `month` (padrão): faixas de anos e meses. `day`: também uma faixa por dia. */
  unit?: GanttUnit;
}

/**
 * Escala do Gantt: o eixo vai de `start` a `end` (inclusive), em dias, e cada faixa (ano, mês, dia) tem `left` e
 * `width` em % do eixo, proporcionais ao número de dias. É a mesma conta do `buildTimeline` dos Gantts de hoje: as
 * posições batem com as que as telas já calculam.
 */
export function buildGanttScale({
  start,
  end,
  unit = "month",
  focusDay,
}: BuildGanttScaleOptions): GanttScale {
  const startDay = toDayNumber(start);
  const endDay = Math.max(toDayNumber(end), startDay);
  const totalDays = endDay - startDay + 1;
  const percent = (days: number) => (days / totalDays) * 100;

  const months: GanttScaleBand[] = [];
  const days: GanttScaleBand[] = [];
  let cursor = startDay;
  while (cursor <= endDay) {
    const date = new Date(cursor * MS_PER_DAY);
    const year = date.getUTCFullYear();
    const month = date.getUTCMonth();
    const last = Math.min(Math.floor(Date.UTC(year, month + 1, 1) / MS_PER_DAY) - 1, endDay);
    const label = GANTT_MONTH_LABELS[month] ?? "";
    months.push({
      key: `${year}-${pad(month + 1)}`,
      label,
      longLabel: `${label} ${year}`,
      left: percent(cursor - startDay),
      width: percent(last - cursor + 1),
    });
    if (unit === "day") {
      for (let day = cursor; day <= last; day += 1) {
        const dayOfMonth = new Date(day * MS_PER_DAY).getUTCDate();
        days.push({
          key: `${year}-${pad(month + 1)}-${pad(dayOfMonth)}`,
          label: String(dayOfMonth),
          longLabel: `${pad(dayOfMonth)}/${pad(month + 1)}`,
          left: percent(day - startDay),
          width: percent(1),
        });
      }
    }
    cursor = last + 1;
  }

  const years: GanttScaleBand[] = [];
  for (const month of months) {
    const year = month.key.slice(0, 4);
    const current = years.at(-1);
    if (current?.key === year) current.width += month.width;
    else
      years.push({ key: year, label: year, longLabel: year, left: month.left, width: month.width });
  }

  return { unit, startDay, endDay, totalDays, years, months, days, focusDay };
}

/** Faixas da unidade da escala (meses ou dias): a largura mínima do eixo é uma por faixa. */
export function scaleUnits(scale: GanttScale): GanttScaleBand[] {
  return scale.unit === "day" ? scale.days : scale.months;
}

/** Início do dia `date` em % do eixo (marcos de data: limite, início); `null` fora do eixo. */
export function positionDate(scale: GanttScale, date: GanttIsoDate): number | null {
  const day = toDayNumber(date);
  if (day < scale.startDay || day > scale.endDay) return null;
  return ((day - scale.startDay) / scale.totalDays) * 100;
}

/** Meio do dia atual em % do eixo (linha Hoje); `null` fora do eixo. */
export function positionToday(scale: GanttScale, today: GanttIsoDate): number | null {
  const day = toDayNumber(today);
  if (day < scale.startDay || day > scale.endDay) return null;
  return ((day - scale.startDay + 0.5) / scale.totalDays) * 100;
}

/** Posição e largura (em %) de um intervalo, recortado ao eixo; `null` quando fica todo fora. */
export function positionRange(scale: GanttScale, { start, end }: GanttRange): GanttPosition | null {
  const first = toDayNumber(start);
  const last = toDayNumber(end);
  if (last < first || last < scale.startDay || first > scale.endDay) return null;

  const from = Math.max(first, scale.startDay);
  const to = Math.min(last, scale.endDay);
  return {
    left: ((from - scale.startDay) / scale.totalDays) * 100,
    width: ((to - from + 1) / scale.totalDays) * 100,
    clippedStart: first < scale.startDay,
    clippedEnd: last > scale.endDay,
  };
}

/** O intervalo como `start`/`end` em % (o formato dos átomos `GanttBar`, `LineMark` e `PillMark` do DS). */
export function rangeToSpan(scale: GanttScale, range: GanttRange): GanttSpan | null {
  const position = positionRange(scale, range);
  return position ? { start: position.left, end: position.left + position.width } : null;
}

/** Altura mínima (rem) de uma linha com `lanes` faixas sobrepostas: a maior entre o mínimo e as faixas mais o respiro. */
export function lanesMinHeightRem({
  lanes,
  laneHeightRem,
  paddingRem,
  minRem,
}: {
  lanes: number;
  laneHeightRem: number;
  paddingRem: number;
  minRem: number;
}): number {
  return Math.max(minRem, lanes * laneHeightRem + paddingRem);
}
